import React, { useEffect, useMemo, useRef, useState } from 'react'
import './index.css'

interface Props {
	id?: string
	value: string
	onChange: (value: string) => void
	disabled?: boolean
	locale?: string
	placeholder?: string
}

// Never use toISOString() to turn a picked day into 'YYYY-MM-DD'. It converts to
// UTC first, so in Zagreb (UTC+1/+2) midnight on the 12th becomes 22:00 on the
// 11th and the couple silently books the wrong day. Format from local parts.
const toValue = (d: Date): string => [
	d.getFullYear(),
	String(d.getMonth() + 1).padStart(2, '0'),
	String(d.getDate()).padStart(2, '0')
].join('-')

// Same trap in reverse: new Date('2026-09-12') is parsed as UTC midnight, which
// is the previous day west of Greenwich. Build it from parts instead.
const fromValue = (value: string): Date | null => {
	const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '')
	if (!parts) return null
	const [, y, m, d] = parts
	return new Date(Number(y), Number(m) - 1, Number(d))
}

const startOfToday = (): Date => {
	const now = new Date()
	return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

// Monday-first. getDay() is Sunday-first, and every locale this app ships in
// starts the week on Monday.
const mondayIndex = (d: Date): number => (d.getDay() + 6) % 7

const DatePicker: React.FC<Props> = ({
	id, value, onChange, disabled, locale = 'hr', placeholder = ''
}) => {
	const selected = fromValue(value)
	const today = startOfToday()

	const [open, setOpen] = useState(false)
	const [view, setView] = useState<Date>(
		selected || new Date(today.getFullYear(), today.getMonth(), 1)
	)
	const wrapRef = useRef<HTMLDivElement>(null)

	// Weekday and month names come from Intl rather than translation keys, so all
	// six languages are covered without a single string to maintain - and a
	// seventh would work the day it is added.
	const weekdays = useMemo(() => {
		const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' })
		// 2024-01-01 was a Monday.
		return Array.from({ length: 7 }, (_, i) =>
			fmt.format(new Date(2024, 0, 1 + i))
		)
	}, [locale])

	const monthLabel = useMemo(() => new Intl.DateTimeFormat(locale, {
		month: 'long', year: 'numeric'
	}).format(view), [locale, view])

	const triggerLabel = useMemo(() => (selected
		? new Intl.DateTimeFormat(locale, {
			day: 'numeric', month: 'long', year: 'numeric'
		}).format(selected)
		: placeholder), [locale, selected, placeholder])

	useEffect(() => {
		if (!open) return

		const onPointer = (e: MouseEvent) => {
			if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}

		document.addEventListener('mousedown', onPointer)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onPointer)
			document.removeEventListener('keydown', onKey)
		}
	}, [open])

	const monthStart = new Date(view.getFullYear(), view.getMonth(), 1)
	const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate()
	const blanks = mondayIndex(monthStart)

	// A wedding cannot be in the past, and the server rejects one with a single
	// generic message that cannot say why. Making the day unclickable is the only
	// place that limit can be explained to the couple.
	const isPast = (day: number) =>
		new Date(view.getFullYear(), view.getMonth(), day) < today

	const canGoBack = new Date(view.getFullYear(), view.getMonth(), 1) > today

	const shiftMonth = (by: number) =>
		setView(new Date(view.getFullYear(), view.getMonth() + by, 1))

	const pick = (day: number) => {
		onChange(toValue(new Date(view.getFullYear(), view.getMonth(), day)))
		setOpen(false)
	}

	const isSelected = (day: number) => Boolean(selected) &&
		selected!.getFullYear() === view.getFullYear() &&
		selected!.getMonth() === view.getMonth() &&
		selected!.getDate() === day

	const isToday = (day: number) =>
		today.getFullYear() === view.getFullYear() &&
		today.getMonth() === view.getMonth() &&
		today.getDate() === day

	return (
		<div className='wp-datepicker' ref={wrapRef}>
			<button
				type='button'
				id={id}
				className={`wp-datepicker-trigger${selected ? '' : ' is-empty'}`}
				disabled={disabled}
				aria-haspopup='dialog'
				aria-expanded={open}
				onClick={() => setOpen(!open)}
			>
				<span>{triggerLabel}</span>
				<svg className='wp-datepicker-icon' viewBox='0 0 24 24' aria-hidden='true'>
					<rect x='3' y='5' width='18' height='16' rx='3' />
					<path d='M8 3v4M16 3v4M3 10h18' />
				</svg>
			</button>

			{open && (
				<div className='wp-datepicker-panel' role='dialog' aria-label={monthLabel}>
					<div className='wp-datepicker-head'>
						<button
							type='button'
							className='wp-datepicker-nav'
							onClick={() => shiftMonth(-1)}
							disabled={!canGoBack}
							aria-label='<'
						>‹</button>
						<span className='wp-datepicker-month'>{monthLabel}</span>
						<button
							type='button'
							className='wp-datepicker-nav'
							onClick={() => shiftMonth(1)}
							aria-label='>'
						>›</button>
					</div>

					<div className='wp-datepicker-weekdays'>
						{weekdays.map((w) => <span key={w}>{w}</span>)}
					</div>

					<div className='wp-datepicker-grid'>
						{Array.from({ length: blanks }, (_, i) =>
							<span key={`b${i}`} className='wp-datepicker-blank' />
						)}
						{Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
							<button
								key={day}
								type='button'
								className={[
									'wp-datepicker-day',
									// Weddings are overwhelmingly on a Saturday, so the weekend
									// is worth finding at a glance.
									mondayIndex(new Date(view.getFullYear(), view.getMonth(), day)) >= 5
										? 'is-weekend'
										: '',
									isSelected(day) ? 'is-selected' : '',
									isToday(day) ? 'is-today' : ''
								].filter(Boolean).join(' ')}
								disabled={isPast(day)}
								aria-selected={isSelected(day)}
								onClick={() => pick(day)}
							>{day}</button>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

export default DatePicker
